import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ProjectReviewService } from './project-review.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';

interface ProjectItem {
  project_id: string;
  project_name: string;
  label?: string;
  selected?: boolean;
}

interface StepItem {
  step_label: string;
  step_name: string;
  step_description: string;
  has_transaction: string;
  project_step_id?: string;
  project_id?: string;
  // UI state
  expanded?: boolean;
  childData?: TransactionItem[];
  childLoading?: boolean;
}

interface TransactionItem {
  entity_details: string;
  le_name: string;
  me_code: string;
  source_system_flag: string;
  adj_desc: string;
  user_desc: string;
  curr: string;
  consol_group_key: string;
  amount: number;
  doc_key: string;
  statement_name: string;
  parent_statement_id?: string;
}

@Component({
    selector: 'gtw-project-review',
    templateUrl: './project-review.component.html',
    styleUrls: ['./project-review.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProjectReviewComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  filterParams: any = {};
  globalService: any;
  appService: any = { USER_SETTINGS: '', SERVER_CONFIG: '' };
  GENERAL_CONFIG: any;

  menuOpen = true;
  tableIndex = 0; // 0 = Transaction By Step, 1 = Transaction Data
  projectListLoading = false;
  tableLoading = false;
  mouseOverSearchBox = false;

  projectList: ProjectItem[] = [];
  selectedProject: ProjectItem | null = null;
  projectSearchTerm = '';

  // Tab 0: Transaction By Step
  transByStepData: StepItem[] = [];

  // Tab 1: Transaction Data
  transData: TransactionItem[] = [];

  // Sorting
  stepSortColumn = 'step_label';
  stepSortAsc = true;
  transSortColumn = 'le_name';
  transSortAsc = true;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private projectReviewService: ProjectReviewService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.GENERAL_CONFIG = header.generalConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.loadProjects();
  }

  // --- Project list ---

  loadProjects(): void {
    if (this.projectListLoading) return;
    this.projectListLoading = true;
    this.cdr.detectChanges();

    const customApiUrl = this.GENERAL_CONFIG.custom_api_url;
    const taxYear = this.filterParams.tax_year;

    this.projectReviewService.getProjectsByTaxYear(customApiUrl, taxYear).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (projects: ProjectItem[]) => {
        // Generate initials for collapsed sidebar
        projects.forEach((project: ProjectItem) => {
          if (project.project_name) {
            const words = project.project_name.split(' ');
            const initials: string[] = [];
            for (let i = 0; i < words.length && initials.length < 2; i++) {
              if (isNaN(Number(words[i]))) {
                initials.push(words[i]);
              }
            }
            if (initials.length === 1) {
              project.label = initials[0].replace(/\d/g, '').substring(0, 2);
            } else if (initials.length >= 2) {
              project.label = initials[0].replace(/\d/g, '')[0] + initials[1].replace(/\d/g, '')[0];
            } else {
              project.label = '??';
            }
          }
        });
        this.projectList = projects;
        this.projectListLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[ProjectReview] Error loading projects:', error);
        this.projectListLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  get filteredProjectList(): ProjectItem[] {
    if (!this.projectSearchTerm || this.projectSearchTerm.trim() === '') {
      return this.projectList;
    }
    const search = this.projectSearchTerm.toLowerCase().trim();
    return this.projectList.filter(p => p.project_name?.toLowerCase().includes(search));
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    this.cdr.detectChanges();
  }

  selectProject(project: ProjectItem): void {
    if (this.tableLoading) return;
    this.projectList.forEach(p => p.selected = false);
    project.selected = true;
    this.selectedProject = project;
    this.loadTableData();
  }

  selectTab(index: number): void {
    if (this.tableLoading) return;
    this.tableIndex = index;
    if (this.selectedProject) {
      this.loadTableData();
    }
    this.cdr.detectChanges();
  }

  // --- Table data ---

  loadTableData(): void {
    if (!this.selectedProject) return;
    this.tableLoading = true;
    this.cdr.detectChanges();

    const customApiUrl = this.GENERAL_CONFIG.custom_api_url;
    const projectId = this.selectedProject.project_id;

    if (this.tableIndex === 0) {
      this.transData = [];
      this.projectReviewService.getProjectSteps(customApiUrl, projectId, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (steps: StepItem[]) => {
          this.transByStepData = steps.map(s => ({ ...s, expanded: false, childData: [], childLoading: false }));
          this.tableLoading = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('[ProjectReview] Error loading project steps:', error);
          this.tableLoading = false;
          this.cdr.detectChanges();
        }
      );
    } else if (this.tableIndex === 1) {
      this.transByStepData = [];
      this.projectReviewService.getTransactionData(customApiUrl, projectId, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: TransactionItem[]) => {
          this.transData = data;
          this.tableLoading = false;
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('[ProjectReview] Error loading transaction data:', error);
          this.tableLoading = false;
          this.cdr.detectChanges();
        }
      );
    }
  }

  // --- Expandable child rows (Transaction By Step) ---

  toggleStepExpand(step: StepItem): void {
    if (step.has_transaction !== 'true') return;

    step.expanded = !step.expanded;
    if (step.expanded && (!step.childData || step.childData.length === 0)) {
      this.loadChildData(step);
    }
    this.cdr.detectChanges();
  }

  private loadChildData(step: StepItem): void {
    if (!this.selectedProject) return;
    step.childLoading = true;
    this.cdr.detectChanges();

    const customApiUrl = this.GENERAL_CONFIG.custom_api_url;
    this.projectReviewService.getTransactionDataForProjectStep(
      customApiUrl,
      this.selectedProject.project_id,
      step.project_step_id || '',
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: TransactionItem[]) => {
        step.childData = data;
        step.childLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[ProjectReview] Error loading child data:', error);
        step.childLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  // --- Amount formatting ---

  formatAmount(amount: number): string {
    if (amount === 0 || amount === null || amount === undefined) return '';
    const absAmount = Math.abs(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (amount < 0) {
      return '(' + absAmount + ')';
    }
    return absAmount;
  }

  isNegative(amount: number): boolean {
    return amount < 0;
  }

  // --- Document download ---

  downloadDocument(docKey: string): void {
    if (!docKey) return;
    const docKeys = docKey.split(',');
    docKeys.forEach(key => {
      // Open document download endpoint
      const url = `${this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api_url}/downloadAttachment?doc_key=${key.trim()}`;
      window.open(url, '_blank');
    });
  }

  // --- DST link ---

  getDstLink(row: TransactionItem): string {
    if (!row.statement_name) return '';
    const taxYear = this.filterParams.tax_year;
    const parentStatementId = row.parent_statement_id || row.doc_key;
    if (row.parent_statement_id) {
      return `#/statement-tracker/${taxYear}/${parentStatementId}/${row.doc_key}`;
    }
    return `#/statement-tracker/${taxYear}/${parentStatementId}`;
  }

  // --- Sorting ---

  sortStepTable(column: string): void {
    if (this.stepSortColumn === column) {
      this.stepSortAsc = !this.stepSortAsc;
    } else {
      this.stepSortColumn = column;
      this.stepSortAsc = true;
    }
    this.transByStepData.sort((a: any, b: any) => {
      const valA = a[column] || '';
      const valB = b[column] || '';
      const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
      return this.stepSortAsc ? cmp : -cmp;
    });
    this.cdr.detectChanges();
  }

  sortTransTable(column: string): void {
    if (this.transSortColumn === column) {
      this.transSortAsc = !this.transSortAsc;
    } else {
      this.transSortColumn = column;
      this.transSortAsc = true;
    }
    this.transData.sort((a: any, b: any) => {
      let valA = a[column];
      let valB = b[column];
      if (column === 'amount') {
        valA = valA || 0;
        valB = valB || 0;
        return this.transSortAsc ? valA - valB : valB - valA;
      }
      valA = valA || '';
      valB = valB || '';
      const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
      return this.transSortAsc ? cmp : -cmp;
    });
    this.cdr.detectChanges();
  }
}
